// RunbookService - Tracks operator runbook progress with localStorage persistence

export interface RunbookStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  link?: string; // Optional link to relevant page
}

export interface Runbook {
  id: string;
  title: string;
  description: string;
  category: 'getting-started' | 'deployment' | 'maintenance' | 'incident';
  steps: RunbookStep[];
  estimatedTime: string; // e.g., "15 min"
}

// Storage key for runbook progress
const STORAGE_KEY = 'estream-runbook-progress';
const FIRST_VISIT_KEY = 'estream-first-visit';

// Runbook definitions
const RUNBOOKS: Runbook[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Set up your first lattice and deploy a circuit',
    category: 'getting-started',
    estimatedTime: '20 min',
    steps: [
      { id: 'gs-1', title: 'Connect your wallet', description: 'Authenticate with Spark QR code scan', completed: false, link: '/' },
      { id: 'gs-2', title: 'Review key holders', description: 'Check governance key holder configuration', completed: false, link: '/governance/key-holders' },
      { id: 'gs-3', title: 'Deploy first lattice', description: 'Create a development lattice using Genesis Wizard', completed: false, link: '/genesis' },
      { id: 'gs-4', title: 'Deploy a circuit', description: 'Select a template and deploy to your lattice', completed: false, link: '/circuits' },
      { id: 'gs-5', title: 'Verify deployment', description: 'Check circuit status in the topology view', completed: false, link: '/topology' },
    ],
  },
  {
    id: 'bootstrap-environment',
    title: 'Bootstrap New Environment',
    description: 'Set up a new production or staging environment',
    category: 'deployment',
    estimatedTime: '45 min',
    steps: [
      { id: 'be-1', title: 'Create VPC infrastructure', description: 'Deploy VPC and networking via Infrastructure page', completed: false, link: '/infrastructure' },
      { id: 'be-2', title: 'Configure firewalls', description: 'Set up security groups and firewall rules', completed: false, link: '/infrastructure' },
      { id: 'be-3', title: 'Deploy consensus nodes', description: 'Provision initial consensus node cluster', completed: false, link: '/topology' },
      { id: 'be-4', title: 'Initialize genesis block', description: 'Run Genesis Wizard for the new environment', completed: false, link: '/genesis' },
      { id: 'be-5', title: 'Verify node connectivity', description: 'Check all nodes are synced and healthy', completed: false, link: '/observability' },
      { id: 'be-6', title: 'Configure monitoring', description: 'Set up alerts and dashboards', completed: false, link: '/observability' },
    ],
  },
  {
    id: 'add-node',
    title: 'Add a Node to the Network',
    description: 'Provision and join a new node to an existing lattice',
    category: 'deployment',
    estimatedTime: '15 min',
    steps: [
      { id: 'an-1', title: 'Provision VM', description: 'Create new VM with correct specs from Infrastructure', completed: false, link: '/infrastructure' },
      { id: 'an-2', title: 'Generate node identity', description: 'Create ML-DSA-87 keys for the node', completed: false, link: '/governance/key-holders' },
      { id: 'an-3', title: 'Submit join request', description: 'Create governance proposal for node admission', completed: false, link: '/governance' },
      { id: 'an-4', title: 'Collect approvals', description: 'Get required key holder signatures via Spark', completed: false, link: '/governance/approvals' },
      { id: 'an-5', title: 'Verify node status', description: 'Confirm node appears in topology and is syncing', completed: false, link: '/topology' },
    ],
  },
  {
    id: 'upgrade-nodes',
    title: 'Upgrade All Nodes to New Version',
    description: 'Rolling upgrade process for network nodes',
    category: 'maintenance',
    estimatedTime: '30 min',
    steps: [
      { id: 'un-1', title: 'Review release notes', description: 'Check changelog and breaking changes', completed: false, link: '/releases' },
      { id: 'un-2', title: 'Create upgrade proposal', description: 'Submit governance proposal for version upgrade', completed: false, link: '/governance' },
      { id: 'un-3', title: 'Approve upgrade', description: 'Collect required signatures from key holders', completed: false, link: '/governance/approvals' },
      { id: 'un-4', title: 'Monitor rollout', description: 'Track node upgrade progress in topology', completed: false, link: '/topology' },
      { id: 'un-5', title: 'Verify version', description: 'Confirm all nodes running new version', completed: false, link: '/observability' },
    ],
  },
  {
    id: 'rotate-keys',
    title: 'Rotate Keys',
    description: 'Rotate governance or node cryptographic keys',
    category: 'maintenance',
    estimatedTime: '20 min',
    steps: [
      { id: 'rk-1', title: 'Generate new keys', description: 'Create new ML-DSA-87 key pair', completed: false, link: '/governance/key-holders' },
      { id: 'rk-2', title: 'Submit rotation proposal', description: 'Create governance proposal for key rotation', completed: false, link: '/governance' },
      { id: 'rk-3', title: 'Approve rotation', description: 'Collect signatures from other key holders', completed: false, link: '/governance/approvals' },
      { id: 'rk-4', title: 'Verify new key active', description: 'Confirm new key is registered on-chain', completed: false, link: '/governance/key-holders' },
      { id: 'rk-5', title: 'Revoke old key', description: 'Submit proposal to deactivate old key', completed: false, link: '/governance' },
    ],
  },
  {
    id: 'incident-node-offline',
    title: 'Incident Response: Node Offline',
    description: 'Steps to diagnose and recover an offline node',
    category: 'incident',
    estimatedTime: '15 min',
    steps: [
      { id: 'ir-1', title: 'Check node status', description: 'View node in topology for error details', completed: false, link: '/topology' },
      { id: 'ir-2', title: 'Review logs', description: 'Check observability for error patterns', completed: false, link: '/observability' },
      { id: 'ir-3', title: 'Check infrastructure', description: 'Verify VM and network connectivity', completed: false, link: '/infrastructure' },
      { id: 'ir-4', title: 'Restart node', description: 'Issue restart command if needed', completed: false, link: '/infrastructure' },
      { id: 'ir-5', title: 'Verify recovery', description: 'Confirm node is back online and syncing', completed: false, link: '/topology' },
      { id: 'ir-6', title: 'Document incident', description: 'Record incident in audit log', completed: false, link: '/governance' },
    ],
  },
];

// Map page paths to relevant runbook step IDs for contextual help
export const PAGE_RUNBOOK_MAP: Record<string, string[]> = {
  '/': ['gs-1', 'gs-2'],
  '/genesis': ['gs-3', 'be-4'],
  '/topology': ['gs-5', 'an-5', 'un-4', 'ir-1', 'ir-5'],
  '/circuits': ['gs-4'],
  '/infrastructure': ['be-1', 'be-2', 'an-1', 'ir-3', 'ir-4'],
  '/governance': ['an-3', 'un-2', 'rk-2', 'rk-5', 'ir-6'],
  '/governance/approvals': ['an-4', 'un-3', 'rk-3'],
  '/governance/key-holders': ['gs-2', 'an-2', 'rk-1', 'rk-4'],
  '/releases': ['un-1'],
  '/observability': ['be-5', 'be-6', 'un-5', 'ir-2'],
};

// Get runbook progress from storage
function getStoredProgress(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Save runbook progress to storage
function saveProgress(progress: Record<string, boolean>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch {
    console.warn('Failed to save runbook progress');
  }
}

// Check if this is the first visit
export function isFirstVisit(): boolean {
  return localStorage.getItem(FIRST_VISIT_KEY) !== 'false';
}

// Mark first visit as complete
export function markFirstVisitComplete(): void {
  localStorage.setItem(FIRST_VISIT_KEY, 'false');
}

// Get all runbooks with current progress
export function getRunbooks(): Runbook[] {
  const progress = getStoredProgress();
  
  return RUNBOOKS.map(runbook => ({
    ...runbook,
    steps: runbook.steps.map(step => ({
      ...step,
      completed: progress[step.id] ?? step.completed,
    })),
  }));
}

// Get a single runbook by ID
export function getRunbook(id: string): Runbook | undefined {
  const runbooks = getRunbooks();
  return runbooks.find(r => r.id === id);
}

// Toggle step completion
export function toggleStepCompletion(stepId: string): void {
  const progress = getStoredProgress();
  progress[stepId] = !progress[stepId];
  saveProgress(progress);
}

// Mark step as complete
export function markStepComplete(stepId: string): void {
  const progress = getStoredProgress();
  progress[stepId] = true;
  saveProgress(progress);
}

// Get runbook completion percentage
export function getRunbookProgress(runbookId: string): number {
  const runbook = getRunbook(runbookId);
  if (!runbook) return 0;
  
  const completed = runbook.steps.filter(s => s.completed).length;
  return Math.round((completed / runbook.steps.length) * 100);
}

// Get overall progress across all runbooks
export function getOverallProgress(): { completed: number; total: number; percentage: number } {
  const runbooks = getRunbooks();
  let completed = 0;
  let total = 0;
  
  for (const runbook of runbooks) {
    for (const step of runbook.steps) {
      total++;
      if (step.completed) completed++;
    }
  }
  
  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
}

// Get contextual runbook steps for a page
export function getContextualSteps(pathname: string): { runbook: Runbook; step: RunbookStep }[] {
  const stepIds = PAGE_RUNBOOK_MAP[pathname] || [];
  const runbooks = getRunbooks();
  const results: { runbook: Runbook; step: RunbookStep }[] = [];
  
  for (const runbook of runbooks) {
    for (const step of runbook.steps) {
      if (stepIds.includes(step.id)) {
        results.push({ runbook, step });
      }
    }
  }
  
  return results;
}

// Reset all progress
export function resetProgress(): void {
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(FIRST_VISIT_KEY);
}

// Get next incomplete step for a runbook
export function getNextIncompleteStep(runbookId: string): RunbookStep | undefined {
  const runbook = getRunbook(runbookId);
  if (!runbook) return undefined;
  
  return runbook.steps.find(s => !s.completed);
}

// Subscribe to progress changes
type ProgressListener = () => void;
const listeners: Set<ProgressListener> = new Set();

export function subscribeToProgress(listener: ProgressListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function notifyProgressChange(): void {
  listeners.forEach(listener => listener());
}

// Export types
export type { ProgressListener };
